import React, { useEffect, useState } from 'react';
import SphereEditorOrganism from '../../organisms/SphereEditor/SphereEditor';
import { Sphere, UpdateAck } from 'types/global';
import { initializeSphere } from '@center-platform/spheres/sphere';
import { buildPerspectiveAssignments } from '@center-platform/spheres/identity';
import './AddSphere.scss';
import { useSelector } from 'react-redux';
import { RootState } from '@center-pwa/store/store';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useSchemaType } from '../../../hooks/useSchemaType';

interface AddSphereProps {
  parentSphere: Sphere;
  grandparentSphere?: Sphere; // Optional grandparent sphere
  initialSphere?: Partial<Sphere>; // Optional initial sphere data
  onSave: (sphere: Sphere, parentId: string, ancestorIds?: string[]) => void; // Callback for saving the sphere
  editable?: boolean; // Whether the editor is editable
  schemaSystemType?: string; // Optional predefined type for the sphere
  onClose: () => void; // Callback for closing the dialog
  useCases?: string[]; // Optional use cases for the Sphere Type Selector
  schema?: any;
}

const AddSphere: React.FC<AddSphereProps> = React.memo(({
  parentSphere,
  grandparentSphere,
  initialSphere = {},
  onSave,
  editable = true,
  schemaSystemType,
  onClose,
  schema,
  useCases = [], // Optional use cases for the Sphere Type Selector
}) => {
  console.log('[AddSphere] RENDERING with parentSphere.id:', parentSphere?.id, 'editable:', editable);

  // Hooks must be called unconditionally - move them before any early returns
  const apiClients = useApiClientsOrNull();
  const userSphere = useSelector((state: RootState) => state.userProfile.userSphere);
  const parentSphereTypeRaw = parentSphere?.properties?.type;
  const parentSphereType = (Array.isArray(parentSphereTypeRaw) ? parentSphereTypeRaw[0] : parentSphereTypeRaw as string | undefined) ?? null;
  const { schema: parentSchema, status: parentSchemaStatus } = useSchemaType(parentSphereType);

  if (!apiClients) {
    return null;
  }

  const { graphClient } = apiClients;
  console.log('addsphere schema', schema);
  if (!parentSphere) {
    throw new Error('Parent Sphere is required!');
  }

  console.log('[AddSphere] parentSphereType', parentSphereType);

  const [isSphereSubmitting, setIsSphereSubmitting] = useState(false);

  // Sphere state, initialized if schemaSystemType is provided
  const [sphere, setSphere] = useState<Sphere | null>(null);
  // ⭐ NEW: If schema is passed (template use), initialize sphere on mount!
  useEffect(() => {
    if (schema && parentSphere && userSphere &&
        (parentSchemaStatus === 'succeeded' || parentSchemaStatus === 'notFound')) {
      const perspectiveAssignments = buildPerspectiveAssignments(
        schema,
        parentSphere,
        parentSchema ? [parentSchema] : [],
      );
      const initializedSphere = initializeSphere(
        schema,
        parentSphere,
        userSphere,
        perspectiveAssignments,
      );

      console.log('[AddSphere] Initialized sphere with buildPerspectiveAssignments:', {
        sphereId: initializedSphere.id,
        sphereName: initializedSphere.properties.name,
        assignedPerspectives: initializedSphere.assignedPerspectives,
        center_center: initializedSphere.assignedPerspectives?.find(ap => ap.perspectiveProperties.system_name === 'center_center'),
        center_parent: initializedSphere.assignedPerspectives?.find(ap => ap.perspectiveProperties.system_name === 'center_parent'),
      });
      setSphere(initializedSphere);
    }
  }, [schema, parentSphere, userSphere, parentSchema, parentSchemaStatus]);
  // inside the AddSphere component
  const handleUpdate = async (updatedSphere: Sphere): Promise<UpdateAck> => {
    setIsSphereSubmitting(true);

    try {
      // Persist local draft immediately so the UI reflects changes
      setSphere(updatedSphere);

      console.log('[AddSphere] handleUpdate - submitting sphere:', {
        sphereId: updatedSphere.id,
        sphereName: updatedSphere.properties.name,
        assignedPerspectives: updatedSphere.assignedPerspectives,
        center_center: updatedSphere.assignedPerspectives?.find(ap => ap.perspectiveProperties.system_name === 'center_center'),
      });

      const resp = await graphClient.post<{ status: string; sphere?: Sphere; ancestorIds?: string[] }>('/spheres', {
        schemaSystemName: updatedSphere.properties.type,
        sphere: updatedSphere,
      });

      if (resp.status === 'success' && resp.sphere) {
        // Server returned the complete sphere with ID and any generated properties (like imageId)
        // Use the server's sphere data instead of the local updatedSphere
        onSave(resp.sphere, parentSphere.id, resp.ancestorIds);
        onClose();
        return { success: true };
      }

      return { success: false, message: 'Save failed on server' };
    } catch (error: any) {
      const msg = error?.message ?? 'An unexpected error occurred.';
      console.error('Error adding sphere:', msg);
      return { success: false, message: msg };
    } finally {
      setIsSphereSubmitting(false);
    }
  };

  // Updated handleUpdate to handle createSphere appropriately and manage failures

  const alwaysAllowPermissions = async (sphereId: string, nodeLabel: string, permissions?: string[]) => {
    return {
      permissions: {
        view: true,
        edit: true,
        delete: true,
      },
    };
  };

  return (
    <div className="add-sphere">
      {/* If schema exists, always show the editor */}
      {schema ? (
        sphere ? (
          <SphereEditorOrganism
            sphere={sphere}
            onUpdate={handleUpdate}
            editable={editable}
            parentSphereType={parentSphere.properties.type}
            onClose={onClose}
            isSphereSubmitting={isSphereSubmitting}
            fetchPermissionList={alwaysAllowPermissions}
          />
        ) : (
          <div>Loading...</div>
        )
      ) : (
        // fallback to your SphereTypeSelector step if needed, otherwise can remove
        <div>Missing schema. Please select a template.</div>
      )}
    </div>
  );
});

AddSphere.displayName = 'AddSphere';

export default AddSphere;
